import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { updateModelGuidance } from '@/pages/Beta/services/modelGuidanceService';
import { errorReporter } from '@/services/errorReportingService';

export const MODEL_GUIDANCE_MAX_LENGTH = 300;

export const useModelGuidance = () => {
  const { userData, userToken, fetchAndSetUserData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);

  const [value, setValue] = useState('');
  const [lastSavedValue, setLastSavedValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const initialValue = userData?.model_guidance ?? '';
    setValue(initialValue);
    setLastSavedValue(initialValue);
  }, [userData?.model_guidance]);

  const handleChange = useCallback((nextValue: string) => {
    setValue(nextValue.slice(0, MODEL_GUIDANCE_MAX_LENGTH));
  }, []);

  const handleSave = useCallback(async () => {
    if (!userData?.id || !userToken) {
      showAlert('Unable to save your guidance right now. Please try again later.', 'error');
      return;
    }

    setIsSaving(true);
    const sanitizedValue = value.trim();

    try {
      await updateModelGuidance({
        userId: userData.id,
        token: userToken,
        modelGuidance: sanitizedValue ? sanitizedValue : null,
      });

      // Refresh user data to ensure consistency
      try {
        await fetchAndSetUserData({ force: true });
        // Only update local state and show success if refresh succeeds
        setLastSavedValue(sanitizedValue);
        setValue(sanitizedValue);
        showAlert('Your guidance has been saved.', 'success');
      } catch (refreshError) {
        errorReporter.expected('Failed to refresh user data after updating model guidance', refreshError, {
          feature: 'settings',
          operation: 'refreshAfterModelGuidance',
        });
        // Still update local state since the save succeeded
        setLastSavedValue(sanitizedValue);
        setValue(sanitizedValue);
        showAlert(
          'Your guidance was saved, but we could not refresh your data. Please reload the page to see the latest information.',
          'warning',
        );
      }
    } catch (error) {
      errorReporter.expected('Failed to update model guidance', error, {
        feature: 'settings',
        operation: 'updateModelGuidance',
      });
      showAlert('Failed to save your guidance. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [fetchAndSetUserData, showAlert, userData?.id, userToken, value]);

  const isDirty = value.trim() !== lastSavedValue.trim();

  return {
    value,
    onChange: handleChange,
    onSave: handleSave,
    isSaving,
    isDirty,
    maxLength: MODEL_GUIDANCE_MAX_LENGTH,
  };
};
